import React from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  LinearProgress,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  AutoAwesome,
  Cancel,
  CheckCircle,
  Done,
  Groups,
  HourglassEmpty,
  Sync,
} from "@mui/icons-material";
import { MatchingRun, MatchingRunStatus } from "../../types";

interface Props {
  run: MatchingRun;
  onOpenPreview: () => void;
  onTriggerMatching: () => void;
  onOpenWizard: () => void;
  /** Called when instructor cancels (hard-deletes) the run. SA/PR/RD only. */
  onCancelRun?: () => void;
}

const STATUS_LABEL: Record<MatchingRunStatus, string> = {
  [MatchingRunStatus.Draft]: "Draft",
  [MatchingRunStatus.SurveyActive]: "Survey Active",
  [MatchingRunStatus.Processing]: "Processing…",
  [MatchingRunStatus.Ready]: "Results Ready",
  [MatchingRunStatus.Applied]: "Teams Applied",
  [MatchingRunStatus.LmsSynced]: "Synced to LMS",
};

const STATUS_COLOR: Record<
  MatchingRunStatus,
  "default" | "info" | "warning" | "success" | "primary"
> = {
  [MatchingRunStatus.Draft]: "default",
  [MatchingRunStatus.SurveyActive]: "info",
  [MatchingRunStatus.Processing]: "warning",
  [MatchingRunStatus.Ready]: "primary",
  [MatchingRunStatus.Applied]: "success",
  [MatchingRunStatus.LmsSynced]: "success",
};

/** Statuses where a run can still be hard-deleted (not yet applied to course groups). */
const CANCELABLE_STATUSES = new Set<MatchingRunStatus>([
  MatchingRunStatus.Draft,
  MatchingRunStatus.SurveyActive,
  MatchingRunStatus.Processing,
  MatchingRunStatus.Ready,
]);

export function MatchingResultCard({
  run,
  onOpenPreview,
  onTriggerMatching,
  onOpenWizard,
  onCancelRun,
}: Props) {
  const isProcessing = run.status === MatchingRunStatus.Processing;
  const isReady = run.status === MatchingRunStatus.Ready;
  const isApplied =
    run.status === MatchingRunStatus.Applied ||
    run.status === MatchingRunStatus.LmsSynced;
  const isSurveyActive = run.status === MatchingRunStatus.SurveyActive;
  const isCancelable = CANCELABLE_STATUSES.has(run.status);

  return (
    <Card variant="outlined" sx={{ mb: 2 }}>
      <CardContent>
        <Box
          display="flex"
          alignItems="center"
          justifyContent="space-between"
          mb={1}
        >
          <Box display="flex" alignItems="center" gap={1}>
            <Groups color="primary" />
            <Typography variant="h6" component="span">
              Team Matching
            </Typography>
          </Box>
          <Chip
            label={STATUS_LABEL[run.status]}
            color={STATUS_COLOR[run.status]}
            size="small"
            icon={isProcessing ? <CircularProgress size={12} /> : undefined}
          />
        </Box>

        {isSurveyActive && (
          <>
            <Box display="flex" alignItems="center" gap={1} mb={0.5}>
              <Typography variant="body2" color="text.secondary">
                Survey responses
              </Typography>
              <Typography variant="body2" fontWeight={600}>
                {run.responseCount} / {run.tokenCount}
              </Typography>
            </Box>
            <LinearProgress
              variant="determinate"
              value={run.responseRate}
              sx={{ mb: 1, borderRadius: 1 }}
            />
            {run.surveyDueDate && (
              <Typography variant="caption" color="text.secondary">
                Due {new Date(run.surveyDueDate).toLocaleDateString()}
              </Typography>
            )}
          </>
        )}

        {run.errorMessage && (
          <Alert severity="error" sx={{ mb: 1 }}>
            {run.errorMessage}
          </Alert>
        )}

        {run.score != null && (
          <Typography
            variant="caption"
            color="text.secondary"
            display="block"
            mb={1}
          >
            Match quality score: {(run.score * 100).toFixed(1)}%
          </Typography>
        )}

        <Box display="flex" gap={1} flexWrap="wrap" alignItems="center" mt={1}>
          {isSurveyActive && (
            <Tooltip title="Run matching engine now">
              <Button
                size="small"
                variant="outlined"
                startIcon={<AutoAwesome />}
                onClick={onTriggerMatching}
              >
                Run Matching
              </Button>
            </Tooltip>
          )}

          {isProcessing && (
            <Button
              size="small"
              variant="outlined"
              disabled
              startIcon={<HourglassEmpty />}
            >
              Processing…
            </Button>
          )}

          {isReady && (
            <Button
              size="small"
              variant="contained"
              startIcon={<Done />}
              onClick={onOpenPreview}
            >
              Review Teams
            </Button>
          )}

          {isApplied && (
            <Button
              size="small"
              variant="outlined"
              startIcon={<CheckCircle />}
              onClick={onOpenPreview}
            >
              View Teams
            </Button>
          )}

          <Button
            size="small"
            variant="text"
            startIcon={<Sync />}
            onClick={onOpenWizard}
          >
            Settings
          </Button>

          {/* Right-align cancel button */}
          {isCancelable && onCancelRun && (
            <Box sx={{ ml: "auto" }}>
              <Tooltip title="Delete this matching run and start fresh">
                <Button
                  size="small"
                  variant="outlined"
                  color="error"
                  startIcon={<Cancel />}
                  onClick={onCancelRun}
                >
                  Cancel Matching
                </Button>
              </Tooltip>
            </Box>
          )}
        </Box>
      </CardContent>
    </Card>
  );
}
